import * as THREE from 'three';
import { LayerData, MaterialParams, UIControls } from '../types';
import { ensurePBR, colorToHex } from '../utils';

/**
 * Кастомная замена шейдерного чанка normal_fragment_maps, которая поддерживает
 * смешивание нормал-карты материала (тайлинг) с запечённой нормал-картой модели.
 * Используется через material.onBeforeCompile.
 */
const BLENDED_NORMAL_FRAGMENT_MAPS = /* glsl */`
#ifdef USE_NORMALMAP_OBJECTSPACE

  normal = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;

  #ifdef FLIP_SIDED
    normal = - normal;
  #endif

  #ifdef DOUBLE_SIDED
    normal = normal * faceDirection;
  #endif

  normal = normalize( normalMatrix * normal );

#elif defined( USE_NORMALMAP_TANGENTSPACE )

  vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;
  mapN.xy *= normalScale;

  // Смешиваем с запечённой нормал-картой модели (UDN additive blending)
  // vModelNormalUv — raw UV0 без тайлинга, инжектируется через onBeforeCompile
  vec3 modelMapN = texture2D( modelNormalMap, vModelNormalUv ).xyz * 2.0 - 1.0;
  modelMapN.xy *= modelNormalScale;
  mapN = normalize( vec3( mapN.xy + modelMapN.xy, mapN.z ) );

  normal = normalize( tbn * mapN );

#elif defined( USE_BUMPMAP )

  normal = perturbNormalArb( - vViewPosition, normal, dHdxy_fwd(), faceDirection );

#endif
`;

const DEFAULT_MATERIAL_PARAMS: MaterialParams = {
  baseColor: '#ffffff',
  tiling: 1,
  rotationDeg: 0,
  // envIntensity не включается в дефолты - это глобальная настройка
  roughness: 0.7,
  roughnessMul: 1,
  metalness: 0.15,
  normalScale: 2,
  opacity: 1
};

export class MaterialManager {
  private selectedMesh: THREE.Mesh | null = null;
  private selectedMeshes: THREE.Mesh[] = []; // Мультивыбор
  private projectLayers: Record<string, LayerData> = {};
  private uiControls: UIControls;
  private currentModel: THREE.Group | null = null;
  public sceneManager: any;
  private isSelectingLayer: boolean = false; // Флаг для блокировки applyMaterialUI при переключении слоя

  constructor(uiControls: UIControls) {
    this.uiControls = uiControls;
    this.setupEventListeners();
  }
  
  // Установить список выбранных мешей для мультивыбора
  public setSelectedMeshes(meshes: THREE.Mesh[]): void {
    this.selectedMeshes = meshes;
  }
  
  public getSelectedMeshes(): THREE.Mesh[] {
    return this.selectedMeshes;
  }

  private setupEventListeners(): void {
    const controls = ['color', 'tiling', 'rot', 'rough', 'roughTex', 'metal', 'norm'];
    controls.forEach(key => {
      const control = this.uiControls[key as keyof UIControls];
      if (control) {
        control.addEventListener('input', () => {
          // В мультивыделении, если у слоёв разные baseColor — при изменении ползунков
          // НЕ схлопываем цвета в один. Общий цвет применяем только когда меняют сам color.
          const preserveColors = key !== 'color' && this.hasMixedSelectedBaseColors();
          this.applyMaterialUI({ preserveColors });
          this.updateUIValueHints();
        });
      }
    });
    
    // Reflectivity (env) - применяется ко всей модели
    const envControl = this.uiControls.env;
    if (envControl) {
      envControl.addEventListener('input', () => {
        this.applyGlobalReflectivity();
        this.updateUIValueHints();
      });
    }
  }

  private getLayerIdForMesh(mesh: THREE.Mesh): string | null {
    const isPrint = mesh.name?.startsWith('Print_');
    return isPrint ? (mesh as any).userData?.printId : mesh.uuid;
  }

  private getLayerBaseColorHex(mesh: THREE.Mesh, layerId: string): string {
    const smBase = (this.sceneManager as any)?.project?.layers?.[layerId]?.params?.baseColor;
    if (typeof smBase === 'string' && smBase.trim()) return smBase.trim();

    const stBase = this.projectLayers[layerId]?.params?.baseColor;
    if (typeof stBase === 'string' && stBase.trim()) return stBase.trim();

    const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
    const mat = ensurePBR(material) as any;
    if (mat?.color) return colorToHex(mat.color);
    return '#ffffff';
  }

  private hasMixedSelectedBaseColors(): boolean {
    // Только для мультивыделения (иначе смысла нет)
    const selected = this.selectedMeshes.length > 0
      ? this.selectedMeshes
      : (this.selectedMesh ? [this.selectedMesh] : []);
    if (selected.length <= 1) return false;

    const colors = new Set<string>();
    for (const mesh of selected) {
      const layerId = this.getLayerIdForMesh(mesh);
      if (!layerId) continue;
      colors.add(this.getLayerBaseColorHex(mesh, layerId).toLowerCase());
      if (colors.size > 1) return true;
    }
    return false;
  }

  public setSelectedMesh(mesh: THREE.Mesh | null): void {
    this.selectedMesh = mesh;
  }

  public getSelectedMesh(): THREE.Mesh | null {
    return this.selectedMesh;
  }

  /**
   * Сбрасывает материал выбранного меша к изначальному состоянию
   * Поддерживает мультивыбор - сбрасывает материалы для всех выбранных мешей
   */
  public resetMaterial(): void {
    // Определяем список мешей для сброса
    const meshesToReset: THREE.Mesh[] = [];
    
    if (this.selectedMeshes.length > 0) {
      // Мультивыбор - сбрасываем все выбранные меши
      meshesToReset.push(...this.selectedMeshes);
    } else if (this.selectedMesh) {
      // Одиночный выбор - сбрасываем только выбранный меш
      meshesToReset.push(this.selectedMesh);
    } else {
      // Нет выбранных мешей
      return;
    }

    // Сбрасываем материалы для всех мешей
    meshesToReset.forEach(mesh => {
      const isPrint = mesh.name.startsWith('Print_');
      const layerId = isPrint ? (mesh as any).userData?.printId : mesh.uuid;
      if (!layerId) return;

      const state = this.projectLayers[layerId] ?? (this.projectLayers[layerId] = {
        name: mesh.name || '',
        maps: {},
        params: { ...DEFAULT_MATERIAL_PARAMS },
        preset: null,
        type: isPrint ? 'print' : 'layer'
      });

      // Сбрасываем параметры согласно требованиям: белый цвет, никаких текстур, параметры из preset
      state.params = {
        baseColor: '#ffffff', // Белый цвет
        tiling: 8,
        rotationDeg: 0,
        roughness: 0.7,
        roughnessMul: 1.0,
        metalness: 0.15,
        normalScale: 0.35,
        opacity: 1
      };

      // Удаляем ВСЕ текстуры (ни color, ни normal, ни других)
      if (isPrint) {
        // Для print оставляем только карту принта (map), остальные удаляем
        const existingMap = state.maps.map;
        state.maps = { map: existingMap || null };
      } else {
        // Для обычных слоев удаляем все текстуры
        state.maps = {};
      }
      state.preset = null;
    });

    // Проставляем UI значения (один раз для всех)
    this.uiControls.color.value = '#ffffff'; // Белый цвет
    this.uiControls.tiling.value = '8';
    this.uiControls.rot.value = '0';
    // envIntensity (Reflectivity) не меняется при сбросе - это глобальная настройка
    this.uiControls.rough.value = '0.7';
    this.uiControls.roughTex.value = '1.0';
    this.uiControls.metal.value = '0.15';
    this.uiControls.norm.value = '0.35';

    // Применяем параметры к материалам всех выбранных мешей
    this.applyMaterialUI();
  }
  
  // getSelectedPrintTexture method removed - editing prints is disabled

  public getProjectLayers(): Record<string, LayerData> {
    return this.projectLayers;
  }

  public setProjectLayers(layers: Record<string, LayerData>): void {
    this.projectLayers = layers;
  }

  public getUIControls(): UIControls {
    return this.uiControls;
  }

  public setCurrentModel(model: THREE.Group | null): void {
    this.currentModel = model;
  }

  public applyGlobalReflectivity(): void {
    if (!this.currentModel) return;
    
    const envIntensity = parseFloat(this.uiControls.env.value || '0');
    
    // Применяем ко всем материалам модели
    this.currentModel.traverse((object) => {
      if ((object as any).isMesh) {
        const mesh = object as THREE.Mesh;
        const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        
        materials.forEach(material => {
          ensurePBR(material);
          const mat = material as any;
          if (mat.envMapIntensity !== undefined) {
            mat.envMapIntensity = envIntensity;
          }
        });
      }
    });
  }

  private getAllPrintMeshes(): THREE.Mesh[] {
    const printMeshes: THREE.Mesh[] = [];
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        printMeshes.push(obj as THREE.Mesh);
      }
    });
    return printMeshes;
  }

  private getPrintMeshesById(printId: string): THREE.Mesh[] {
    const res: THREE.Mesh[] = [];
    this.currentModel?.traverse((obj) => {
      if ((obj as any).isMesh && obj.name.startsWith('Print_')) {
        const m = obj as THREE.Mesh;
        const meshPrintId = (m as any).userData?.printId;
        if (meshPrintId === printId) res.push(m);
      }
    });
    return res;
  }
  
  public syncLayerState(uuid: string, layerData: LayerData): void {
    // Синхронизация состояния слоя из SceneManager
    if (!this.projectLayers[uuid]) {
      this.projectLayers[uuid] = layerData;
    } else {
      // Обновляем карту принта (map) только если её ещё не было —
      // для принт-слоя map задаётся самим принтом
      if (layerData.maps.map && !this.projectLayers[uuid].maps.map) {
        this.projectLayers[uuid].maps.map = layerData.maps.map;
      }

      // Функция для подготовки текстуры (только для реальных THREE.Texture объектов)
      const prepareTexture = (tex: any): boolean => {
        // Пропускаем строки (dataURL) - они будут загружены позже при применении материала
        if (typeof tex === 'string') {
          return false;
        }
        // Проверяем, что это реальный объект текстуры
        if (!tex || typeof tex !== 'object') {
          return false;
        }
        // Проверяем наличие свойств THREE.Texture
        if (!tex.repeat || typeof tex.repeat.set !== 'function') {
          tex.repeat = new THREE.Vector2(1, 1);
        }
        if (!tex.center || typeof tex.center.set !== 'function') {
          tex.center = new THREE.Vector2(0.5, 0.5);
        }
        return true;
      };

      // Обновляем все PBR карты (normal, color, roughness, ao, metalness, opacity)
      // opacity хранится как maps.opacity и применяется как material.alphaMap
      const pbrMaps = ['normal', 'color', 'roughness', 'ao', 'metalness', 'opacity'] as const;
      
      // ВАЖНО: Сначала удаляем текстуры, которых нет в новом layerData
      // Это предотвращает "перемешивание" текстур от предыдущих материалов
      for (const mapKey of pbrMaps) {
        if (!layerData.maps[mapKey]) {
          // Текстуры нет в новом layerData - удаляем её
          const prev = this.projectLayers[uuid].maps[mapKey] as any;
          if (prev && typeof prev !== 'string') {
            try { prev.dispose?.(); } catch {}
          }
          delete this.projectLayers[uuid].maps[mapKey];
        }
      }
      
      // Теперь обновляем/добавляем текстуры, которые есть в новом layerData
      for (const mapKey of pbrMaps) {
        if (layerData.maps[mapKey]) {
          const mapValue = layerData.maps[mapKey];
          
          // Если это строка (dataURL), сохраняем как есть - текстура будет загружена при применении
          if (typeof mapValue === 'string') {
            this.projectLayers[uuid].maps[mapKey] = mapValue as any;
            continue;
          }
          
          // Освобождаем предыдущую, если отличается
          const prev = this.projectLayers[uuid].maps[mapKey] as any;
          if (prev && prev !== mapValue && typeof prev !== 'string') {
            try { prev.dispose?.(); } catch {}
          }
          
          const texture = mapValue as THREE.Texture;
          prepareTexture(texture);
          this.projectLayers[uuid].maps[mapKey] = texture;
        }
      }
      
      // Обновляем params
      if (layerData.params) {
        this.projectLayers[uuid].params = { ...layerData.params };
      }
    }
  }

  public getSelectedMaterials(): THREE.Material[] {
    if (!this.selectedMesh) return [];
    
    // Если это принт-слой, возвращаем материалы первого меша
    if (this.selectedMesh.name.startsWith('Print_')) {
      const printId = (this.selectedMesh as any).userData?.printId;
      if (printId) {
        const printMeshes = this.getPrintMeshesById(printId);
        if (printMeshes.length > 0) {
          const firstPrintMesh = printMeshes[0];
          if (Array.isArray(firstPrintMesh.material)) {
            return firstPrintMesh.material;
          }
          return [firstPrintMesh.material];
        }
      }
      return [];
    }
    
    if (Array.isArray(this.selectedMesh.material)) {
      return this.selectedMesh.material;
    }
    
    return [this.selectedMesh.material];
  }

  public selectLayer(mesh: THREE.Mesh): void {
    this.isSelectingLayer = true; // Блокируем applyMaterialUI
    this.selectedMesh = mesh;
    
    // Определяем, является ли это принт-слоем
    const isPrint = mesh.name.startsWith('Print_');
    
    // Для принт-мешей используем printId из userData
    const layerId = isPrint ? (mesh as any).userData?.printId : mesh.uuid;
    
    // Получаем состояние из SceneManager (источник истины) или создаём новое
    let state = this.projectLayers[layerId];
    if (this.sceneManager) {
      const smState = (this.sceneManager as any).project?.layers?.[layerId];
      if (smState) {
        // Синхронизируем с данными из SceneManager
        state = this.projectLayers[layerId] = {
          name: smState.name || mesh.name || '',
          maps: { ...smState.maps },
          params: { ...smState.params },
          preset: smState.preset || null,
          type: smState.type || (isPrint ? 'print' : 'layer')
        };
      }
    }
    
    if (!state) {
      state = this.projectLayers[layerId] = {
      name: mesh.name || '',
      maps: {},
        params: { ...DEFAULT_MATERIAL_PARAMS },
      preset: null,
      type: isPrint ? 'print' : 'layer'
      };
    }

    // Для принтов - сохраняем текстуру из материала, если она еще не сохранена
    if (isPrint && !state.maps.map) {
      const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      if (material && (material as any).map) {
        state.maps.map = (material as any).map;
      }
    }
    
    // Показываем/скрываем кнопку редактирования принта
    const editPrintBtn = document.getElementById('edit-print-btn');
    if (editPrintBtn) {
      editPrintBtn.style.display = isPrint ? '' : 'none';
    }

    // Применяем сохраненные параметры слоя к UI
    // Если параметры не сохранены - используем значения по умолчанию или из материала
    if (state.params && Object.keys(state.params).length > 0) {
      this.applyParamsToUI(state.params);
    } else {
      // Для нового слоя - читаем из материала
      const material = Array.isArray(mesh.material) ? mesh.material[0] : mesh.material;
      const pbrMaterial = ensurePBR(material) as THREE.Material;
      const mat = pbrMaterial as any;
      
      this.uiControls.color.value = colorToHex(mat.color);
      if (mat.roughness !== undefined) this.uiControls.rough.value = mat.roughness.toString();
      if (mat.metalness !== undefined) this.uiControls.metal.value = mat.metalness.toString();
      if (mat.normalScale?.isVector2) this.uiControls.norm.value = mat.normalScale.x.toString();
      
      // tiling и rotation - из текстуры или по умолчанию
      const tex = mat.normalMap || mat.map;
      if (tex?.repeat) {
        this.uiControls.tiling.value = tex.repeat.x.toString();
      } else {
        this.uiControls.tiling.value = '8';
      }
      if (tex?.rotation !== undefined) {
        this.uiControls.rot.value = (tex.rotation * 180 / Math.PI).toString();
      } else {
        this.uiControls.rot.value = '0';
      }
    }
    
    // Обновляем Coloris (без триггера applyMaterialUI)
    this.uiControls.color.dispatchEvent(new Event('input', { bubbles: true }));
    
    // Явно обновляем превьюшку Coloris (.clr-field) для цвета материала
    const updateColorPreview = () => {
      const wrapper = this.uiControls.color.parentElement;
      if (wrapper?.classList.contains('clr-field')) {
        wrapper.style.color = this.uiControls.color.value;
        return true;
      }
      return false;
    };
    
    // Пробуем сразу, если не получилось - повторяем с задержкой
    if (!updateColorPreview()) {
      setTimeout(updateColorPreview, 100);
      setTimeout(updateColorPreview, 300);
    }
    
    this.updateUIValueHints();
    
    this.isSelectingLayer = false; // Разблокируем applyMaterialUI
  }

  private applyParamsToUI(params: MaterialParams): void {
    if (params.baseColor) {
      this.uiControls.color.value = params.baseColor;
    }
    if (params.tiling) this.uiControls.tiling.value = params.tiling.toString();
    if (params.rotationDeg !== undefined) this.uiControls.rot.value = params.rotationDeg.toString();
    // envIntensity (Reflectivity) не применяется здесь - это глобальная настройка, не привязана к слою
    if (params.roughness !== undefined) this.uiControls.rough.value = params.roughness.toString();
    if (params.roughnessMul !== undefined) this.uiControls.roughTex.value = params.roughnessMul.toString();
    if (params.metalness !== undefined) this.uiControls.metal.value = params.metalness.toString();
    if (params.normalScale !== undefined) this.uiControls.norm.value = params.normalScale.toString();
  }

  private syncUIWithMaterial(): void {
    if (!this.selectedMesh) return;
    
    const materials = this.getSelectedMaterials();
    if (materials.length === 0) return;
    
    const material = materials[0] as any;
    
    // Читаем реальные значения из материала
    if (material.color) {
      const hexColor = colorToHex(material.color);
      this.uiControls.color.value = hexColor;
      // Диспатчим событие для обновления Coloris
      this.uiControls.color.dispatchEvent(new Event('input', { bubbles: true }));
    }
    
    if (material.roughness !== undefined) {
      this.uiControls.rough.value = material.roughness.toString();
    }
    
    if (material.metalness !== undefined) {
      this.uiControls.metal.value = material.metalness.toString();
    }
    
    // envMapIntensity больше не синхронизируется из материала - это глобальная настройка
    // Значение берется из текущего слайдера
    
    if (material.normalScale) {
      if (material.normalScale.isVector2) {
        this.uiControls.norm.value = material.normalScale.x.toString();
      } else {
        this.uiControls.norm.value = material.normalScale.toString();
      }
    }
    
    // Для принтов: не трогаем map принта (он всегда с repeat=1),
    // берём только normalMap если она есть; иначе оставляем значение из state.params
    const isPrint = this.selectedMesh?.name.startsWith('Print_');
    const texForSync = isPrint ? material.normalMap : (material.normalMap || material.map);
    if (texForSync && texForSync.repeat) {
      this.uiControls.tiling.value = texForSync.repeat.x.toString();
    }
    if (texForSync && texForSync.rotation !== undefined) {
      this.uiControls.rot.value = (texForSync.rotation * 180 / Math.PI).toString();
    }
  }

  public applyMaterialUI(options?: { preserveColors?: boolean }): void {
    if (!this.selectedMesh) return;
    if (this.isSelectingLayer) return; // Не применяем при переключении слоя

    const preserveColors = !!options?.preserveColors;

    // Если есть список выбранных мешей - применяем ко всем
    if (this.selectedMeshes.length > 0) {
      this.selectedMeshes.forEach(mesh => {
        this.applyMaterialToMesh(mesh, { preserveColors });
      });
      return;
    }
    
    // Если selectedMeshes пуст, но selectedMesh есть - применяем к нему
    this.applyMaterialToMesh(this.selectedMesh, { preserveColors });
  }

  private applyMaterialToMesh(mesh: THREE.Mesh, options?: { preserveColors?: boolean }): void {
    const preserveColors = !!options?.preserveColors;
    // Определяем, является ли это принт-слоем
    const isPrint = mesh.name.startsWith('Print_');
    const layerId = isPrint ? (mesh as any).userData?.printId : mesh.uuid;
    
    // Получаем материалы из переданного меша
    const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
    
    const state = this.projectLayers[layerId] ?? (this.projectLayers[layerId] = {
      name: mesh.name || '',
      maps: {},
      params: {},
      preset: null,
      type: isPrint ? 'print' : 'layer'
    });
    
    // Убеждаемся что тип правильный
    state.type = isPrint ? 'print' : 'layer';

    const color = new THREE.Color(this.uiControls.color.value || '#ffffff');
    const tiling = parseInt(this.uiControls.tiling.value || '8');
    const rotation = (parseFloat(this.uiControls.rot.value || '0') * Math.PI / 180);
    const roughness = parseFloat(this.uiControls.rough.value || '0.7') * parseFloat(this.uiControls.roughTex.value || '1');
    const metalness = parseFloat(this.uiControls.metal.value || '0.15');
    // envIntensity больше не применяется здесь - это глобальная настройка
    const normalScale = parseFloat(this.uiControls.norm.value || '1');

    // Для принт-слоев получаем все меши
    let materialsToApply: THREE.Material[] = materials;
    if (state.type === 'print') {
      const printId = (mesh as any).userData?.printId;
      if (!printId) {
        console.error('Print ID not found for print layer');
        return;
      }
      if (!this.sceneManager) {
        console.error('SceneManager not initialized');
        return;
      }
      const printMeshes = this.sceneManager.getPrintMeshesById(printId);
      materialsToApply = [];
      printMeshes.forEach((mesh: THREE.Mesh) => {
        const meshMaterials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
        materialsToApply.push(...meshMaterials);
      });
    }

    // В режиме preserveColors (используется при применении пресета на мультивыбор)
    // мы не трогаем mat.color, а также не перезаписываем сохранённый baseColor слоя значением из UI.
    const existingBaseColorHex = (() => {
      const smBase = (this.sceneManager as any)?.project?.layers?.[layerId]?.params?.baseColor;
      if (typeof smBase === 'string' && smBase.trim()) return smBase.trim();
      const firstMat = materialsToApply[0] as any;
      if (state.params?.baseColor) return state.params.baseColor;
      if (firstMat?.color) return colorToHex(firstMat.color);
      return '#ffffff';
    })();

    // Единая логика применения материалов для всех слоев
    materialsToApply.forEach(material => {
      ensurePBR(material);
      const mat = material as any;
      
      if (!preserveColors && mat.color) mat.color.copy(color);
      mat.roughness = THREE.MathUtils.clamp(roughness, 0, 1);
      mat.metalness = THREE.MathUtils.clamp(metalness, 0, 1);
      // envMapIntensity не применяется здесь - это глобальная настройка, применяется через applyGlobalReflectivity()
      
      // Функция для настройки текстуры (tiling, rotation, wrapping)
      // Пропускает строки (dataURL) и null/undefined
      const setupTexture = (tex: THREE.Texture | null | undefined | string) => {
        if (!tex || typeof tex === 'string') return;
        if (!tex.repeat || !tex.repeat.set) tex.repeat = new THREE.Vector2(1, 1);
        tex.repeat.set(tiling, tiling);
        tex.rotation = rotation;
        tex.wrapS = THREE.RepeatWrapping;
        tex.wrapT = THREE.RepeatWrapping;
        if (!tex.center || !tex.center.set) tex.center = new THREE.Vector2(0.5, 0.5);
        tex.center.set(0.5, 0.5);
        tex.needsUpdate = true;
      };
      
      // Вспомогательная функция для получения текстуры (пропускает строки/dataURL)
      const getTexture = (mapValue: any): THREE.Texture | null => {
        if (!mapValue || typeof mapValue === 'string') return null;
        return mapValue as THREE.Texture;
      };
      
      // Для обычных слоев - применяем все PBR карты
      if (state.type !== 'print') {
        // Color/Albedo map
        const colorTex = getTexture(state.maps.color);
        if (colorTex) {
          mat.map = colorTex;
          setupTexture(mat.map);
        } else {
          mat.map = null;
        }
        
        // Normal map — смешиваем с нормал-картой модели, если она есть
        const normalTex = getTexture(state.maps.normal);
        this.applyNormalMapsWithBlending(mat, normalTex || null, setupTexture);
        
        // Roughness map
        const roughTex = getTexture(state.maps.roughness);
        if (roughTex) {
          mat.roughnessMap = roughTex;
          setupTexture(mat.roughnessMap);
        } else {
          mat.roughnessMap = null;
        }
        
        // AO map НЕ применяем для отдельных слоев - он применяется ко всей модели
        
        // Metalness map
        const metalTex = getTexture(state.maps.metalness);
        if (metalTex) {
          mat.metalnessMap = metalTex;
          setupTexture(mat.metalnessMap);
        } else {
          mat.metalnessMap = null;
        }
        
        // Opacity map (alphaMap)
        const opacityTex = getTexture(state.maps.opacity);
        if (opacityTex) {
          (mat as any).alphaMap = opacityTex;
          setupTexture((mat as any).alphaMap);
          (mat as any).transparent = true;
        } else {
          (mat as any).alphaMap = null;
          (mat as any).transparent = false;
        }
      }
      // Для принт-слоев НЕ ТРОГАЕМ map-текстуру принта, применяем остальные PBR карты
      else {
        // НЕ МЕНЯЕМ mat.map - оставляем оригинальную текстуру принта
        
        // Normal map — принты тоже смешивают с нормал-картой модели
        const normalTex = getTexture(state.maps.normal);
        this.applyNormalMapsWithBlending(mat, normalTex || null, setupTexture);
        
        // Roughness map
        const roughTex = getTexture(state.maps.roughness);
        if (roughTex) {
          mat.roughnessMap = roughTex;
          setupTexture(mat.roughnessMap);
        } else {
          mat.roughnessMap = null;
        }
        
        // AO map НЕ применяем для отдельных слоев - он применяется ко всей модели
        
        // Metalness map
        const metalTex = getTexture(state.maps.metalness);
        if (metalTex) {
          mat.metalnessMap = metalTex;
          setupTexture(mat.metalnessMap);
        } else {
          mat.metalnessMap = null;
        }
        
        // Opacity map (alphaMap)
        const opacityTex = getTexture(state.maps.opacity);
        if (opacityTex) {
          (mat as any).alphaMap = opacityTex;
          setupTexture((mat as any).alphaMap);
          (mat as any).transparent = true;
        } else {
          (mat as any).alphaMap = null;
        }
      }
      
      // Применяем normal scale
      if (mat.normalScale) {
        if (mat.normalScale.isVector2) {
          mat.normalScale.set(normalScale, normalScale);
        } else {
          mat.normalScale = new THREE.Vector2(normalScale, normalScale);
        }
      }
      
      mat.needsUpdate = true;
    });

    // Для принтов: AO применяем единообразно через SceneManager (channel/flipY/intensity по UV-группе),
    // иначе при ручной подстановке aoMap он визуально «съезжает».
    if (state.type === 'print' && this.sceneManager?.applyModelAoMap) {
      void this.sceneManager.applyModelAoMap();
    }

    // Save parameters (envIntensity не сохраняется - это глобальная настройка)
    state.params = {
      baseColor: preserveColors ? existingBaseColorHex : this.uiControls.color.value,
      tiling: Number(this.uiControls.tiling.value),
      rotationDeg: Number(this.uiControls.rot.value),
      // envIntensity не сохраняется в params слоя - это глобальная настройка
      roughness: Number(this.uiControls.rough.value),
      roughnessMul: Number(this.uiControls.roughTex.value),
      metalness: Number(this.uiControls.metal.value),
      normalScale: Number(this.uiControls.norm.value)
    };
    
    // Синхронизируем с SceneManager.project.layers (полная подстановка maps и preset)
    if (this.sceneManager) {
      this.sceneManager.syncLayerParams(layerId, state.params, state.maps, state.preset);
    }

    this.updateUIValueHints();
  }

  /**
   * Публичная версия для вызова из SceneManager.applyLayerData.
   */
  public applyNormalMapsPublic(
    material: THREE.Material,
    materialNormalTex: THREE.Texture | null,
    setupTextureFn: (tex: THREE.Texture) => void
  ): void {
    this.applyNormalMapsWithBlending(material as any, materialNormalTex, setupTextureFn);
  }

  /**
   * Применяет смешивание нормал-карты материала с нормал-картой модели.
   * Вызывается из applyMaterialToMesh вместо прямого `mat.normalMap = ...`.
   *
   * Если обе карты есть — инжектирует onBeforeCompile-блендинг.
   * Если только одна — применяет её напрямую.
   * Если ни одной — сбрасывает normalMap.
   */
  private applyNormalMapsWithBlending(
    mat: any,
    materialNormalTex: THREE.Texture | null,
    setupTextureFn: (tex: THREE.Texture) => void
  ): void {
    const modelNormalMap: THREE.Texture | null =
      mat.userData?.modelNormalMap instanceof THREE.Texture
        ? mat.userData.modelNormalMap
        : null;

    const needsBlend = !!materialNormalTex && !!modelNormalMap;
    const cacheKey = needsBlend
      ? `blend_${modelNormalMap!.uuid}`
      : modelNormalMap
      ? `model_${modelNormalMap.uuid}`
      : '';

    if (needsBlend) {
      // Обе карты: материала (с тайлингом/ротацией) + модели (без тайлинга)
      mat.normalMap = materialNormalTex;
      setupTextureFn(mat.normalMap);
      mat.userData.useNormalBlend = true;
      mat.onBeforeCompile = (shader: any) => {
        shader.uniforms.modelNormalMap = { value: modelNormalMap };
        shader.uniforms.modelNormalScale = { value: new THREE.Vector2(1, 1) };
        // Inject vModelNormalUv varying: raw UV0 without any tiling transform
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_pars_vertex>',
          '#include <uv_pars_vertex>\nvarying vec2 vModelNormalUv;'
        );
        shader.vertexShader = shader.vertexShader.replace(
          '#include <uv_vertex>',
          '#include <uv_vertex>\nvModelNormalUv = uv;'
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <normal_pars_fragment>',
          `#include <normal_pars_fragment>\nuniform sampler2D modelNormalMap;\nuniform vec2 modelNormalScale;\nvarying vec2 vModelNormalUv;`
        );
        shader.fragmentShader = shader.fragmentShader.replace(
          '#include <normal_fragment_maps>',
          BLENDED_NORMAL_FRAGMENT_MAPS
        );
      };
      mat.customProgramCacheKey = () => cacheKey;
    } else if (modelNormalMap && !materialNormalTex) {
      // Только нормал-карта модели: применяем напрямую без тайлинга
      mat.normalMap = modelNormalMap;
      mat.userData.useNormalBlend = false;
      mat.onBeforeCompile = () => {};
      mat.customProgramCacheKey = () => cacheKey;
    } else if (materialNormalTex && !modelNormalMap) {
      // Только нормал-карта материала: стандартное поведение
      mat.normalMap = materialNormalTex;
      setupTextureFn(mat.normalMap);
      mat.userData.useNormalBlend = false;
      mat.onBeforeCompile = () => {};
      mat.customProgramCacheKey = () => cacheKey;
    } else {
      // Нет нормал-карт
      mat.normalMap = null;
      mat.userData.useNormalBlend = false;
      mat.onBeforeCompile = () => {};
      mat.customProgramCacheKey = () => cacheKey;
    }

    mat.needsUpdate = true;
  }

  /**
   * Переприменяет нормал-карты модели для всех уже применённых материалов слоёв.
   * Вызывается из SceneManager после того, как нормал-карты модели были назначены
   * в material.userData.modelNormalMap.
   */
  public reapplyModelNormalMaps(): void {
    if (!this.currentModel) return;

    this.currentModel.traverse((obj) => {
      if (!(obj as any).isMesh) return;
      const mesh = obj as THREE.Mesh;
      if (mesh.name.startsWith('Print_')) return;

      const materials = Array.isArray(mesh.material) ? mesh.material : [mesh.material];
      materials.forEach(material => {
        const mat = material as any;
        // Переустанавливаем только если блендинг уже был настроен
        if (!mat.userData?.useNormalBlend) return;
        const materialNormal = mat.normalMap as THREE.Texture | null;
        const modelNormal = mat.userData?.modelNormalMap as THREE.Texture | null;
        if (!materialNormal || !modelNormal) return;

        // Перекомпилируем шейдер с новой нормал-картой модели
        mat.onBeforeCompile = (shader: any) => {
          shader.uniforms.modelNormalMap = { value: modelNormal };
          shader.uniforms.modelNormalScale = { value: new THREE.Vector2(1, 1) };
          // Inject vModelNormalUv varying: raw UV0 without any tiling transform
          shader.vertexShader = shader.vertexShader.replace(
            '#include <uv_pars_vertex>',
            '#include <uv_pars_vertex>\nvarying vec2 vModelNormalUv;'
          );
          shader.vertexShader = shader.vertexShader.replace(
            '#include <uv_vertex>',
            '#include <uv_vertex>\nvModelNormalUv = uv;'
          );
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_pars_fragment>',
            `#include <normal_pars_fragment>\nuniform sampler2D modelNormalMap;\nuniform vec2 modelNormalScale;\nvarying vec2 vModelNormalUv;`
          );
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            BLENDED_NORMAL_FRAGMENT_MAPS
          );
        };
        mat.customProgramCacheKey = () => `blend_${modelNormal.uuid}`;
        mat.needsUpdate = true;
      });
    });
  }

  private updateUIValueHints(): void {
    const updateHint = (selector: string, value: string) => {
      const element = document.querySelector(selector);
      if (element) element.textContent = value;
    };

    const updateSliderFill = (input: HTMLInputElement | null) => {
      if (!input) return;
      const min = input.min !== '' ? Number(input.min) : 0;
      const max = input.max !== '' ? Number(input.max) : 100;
      const val = Number(input.value);
      const pct = max === min ? 0 : ((val - min) / (max - min)) * 100;
      input.style.setProperty('--slider-value', pct + '%');
    };

    // Обновляем значения справа от ползунков
    updateHint('#u-tiling-value', this.uiControls.tiling.value);
    updateHint('#u-rot-value', this.uiControls.rot.value + '°');
    updateHint('#u-env-value', Number(this.uiControls.env.value).toFixed(2));
    updateHint('#u-rough-value', Number(this.uiControls.rough.value).toFixed(2));
    updateHint('#u-rough-tex-value', Number(this.uiControls.roughTex.value).toFixed(2));
    updateHint('#u-metal-value', Number(this.uiControls.metal.value).toFixed(2));
    updateHint('#u-norm-value', Number(this.uiControls.norm.value).toFixed(2));

    // Обновляем заполнение слайдеров (фон прогресса)
    updateSliderFill(this.uiControls.tiling);
    updateSliderFill(this.uiControls.rot);
    updateSliderFill(this.uiControls.env);
    updateSliderFill(this.uiControls.rough);
    updateSliderFill(this.uiControls.roughTex);
    updateSliderFill(this.uiControls.metal);
    updateSliderFill(this.uiControls.norm);
  }
}
